import { useMemo } from 'react';
import { useReducedMotion } from '../lib/useReducedMotion';

const PALETTE = ['#FFC61A', '#FF9500', '#22D3EE', '#F038C8', '#3DDC64', '#FFFFFF'];

export interface ConfettiProps {
  count?: number;
  /** Скільки секунд «сипати». */
  durationSec?: number;
}

/**
 * Конфеті на CSS — без канвасу і без бібліотек.
 * При prefers-reduced-motion не рендериться взагалі.
 */
export function Confetti({ count = 60, durationSec = 3.5 }: ConfettiProps): React.JSX.Element | null {
  const reduced = useReducedMotion();

  const pieces = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 1.2,
        duration: durationSec * (0.6 + Math.random() * 0.7),
        size: 6 + Math.random() * 10,
        color: PALETTE[i % PALETTE.length] ?? '#FFC61A',
        rotate: Math.random() * 360,
        drift: (Math.random() - 0.5) * 120,
      })),
    [count, durationSec],
  );

  if (reduced) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden" aria-hidden>
      <style>{`
        @keyframes lk-confetti-fall {
          0%   { transform: translate3d(0,-12vh,0) rotate(0deg); opacity: 0; }
          10%  { opacity: 1; }
          100% { transform: translate3d(var(--dx), 108vh, 0) rotate(var(--rot)); opacity: 1; }
        }
      `}</style>
      {pieces.map((piece) => (
        <span
          key={piece.id}
          style={{
            position: 'absolute',
            left: `${piece.left}%`,
            top: 0,
            width: piece.size,
            height: piece.size * 0.45,
            background: piece.color,
            borderRadius: 2,
            ['--dx' as string]: `${piece.drift}px`,
            ['--rot' as string]: `${piece.rotate + 720}deg`,
            animation: `lk-confetti-fall ${piece.duration}s cubic-bezier(.25,.6,.35,1) ${piece.delay}s forwards`,
          }}
        />
      ))}
    </div>
  );
}
